import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type NotesDrawerContextType = {
  open: boolean;
  /** Bumped by every open that asks for the composer, so the drawer can focus it again even when it was already open. */
  composerFocusRequest: number;
  /** Bumped by every open that needs scope-level notes, even when the navigator has a group or task selected. */
  scopeNotesRequest: number;
  openNotesDrawer: (options?: { focusComposer?: boolean; showScopeNotes?: boolean }) => void;
  closeNotesDrawer: () => void;
};

const NotesDrawerContext = createContext<NotesDrawerContextType | null>(null);

/**
 * Whether the notes drawer is open. It only opens and closes here — what it shows follows the scope
 * navigator's selection (see `ScopeNotesDrawer`), except callers can explicitly open the scope level
 * for a scope-wide record such as a handoff note.
 *
 * Optional: hosts without the drawer (the legacy editor, tests that mount one module) get
 * `undefined`, and callers fall back to the older notes flyout.
 */
export const useNotesDrawerOptional = () => useContext(NotesDrawerContext) ?? undefined;

export const NotesDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [composerFocusRequest, setComposerFocusRequest] = useState(0);
  const [scopeNotesRequest, setScopeNotesRequest] = useState(0);

  const openNotesDrawer = useCallback((options?: { focusComposer?: boolean; showScopeNotes?: boolean }) => {
    setOpen(true);
    if (options?.focusComposer) setComposerFocusRequest(request => request + 1);
    if (options?.showScopeNotes) setScopeNotesRequest(request => request + 1);
  }, []);
  const closeNotesDrawer = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ open, composerFocusRequest, scopeNotesRequest, openNotesDrawer, closeNotesDrawer }),
    [open, composerFocusRequest, scopeNotesRequest, openNotesDrawer, closeNotesDrawer],
  );
  return <NotesDrawerContext.Provider value={value}>{children}</NotesDrawerContext.Provider>;
};
